import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import {
  GetEmployeeByIdResponse,
  GetPayrollSettingByEmployeeIdResponse,
} from '@nxsys/types/api/employee';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetActivityLogEmployeeListParams,
  GetActivityLogEmployeeListResponse,
  GetAssignmentEmployeeListParams,
  GetAssignmentEmployeeListResponse,
  GetAttachmentOrderEmployeeListParams,
  GetAttachmentOrderEmployeeListResponse,
  GetBIKListingEmployeeListParams,
  GetBIKListingEmployeeListResponse,
  GetDeductionEmployeeListParams,
  GetDeductionEmployeeListResponse,
  GetDocumentEmployeeListParams,
  GetDocumentEmployeeListResponse,
  GetEmailLogEmployeeListParams,
  GetEmailLogEmployeeListResponse,
  GetEmployeeListParams,
  GetEmployeeListResponse,
  GetExpenseEmployeeListParams,
  GetExpenseEmployeeListResponse,
  GetExpensePotEmployeeListParams,
  GetExpensePotEmployeeListResponse,
  GetHolidayPayEmployeeListParams,
  GetHolidayPayEmployeeListResponse,
  GetLeaverHistoryEmployeeListParams,
  GetLeaverHistoryEmployeeListResponse,
  GetMessageHistoryEmployeeListParams,
  GetMessageHistoryEmployeeListResponse,
  GetNoteEmployeeListParams,
  GetNoteEmployeeListResponse,
  GetPaymentEmployeeListParams,
  GetPaymentEmployeeListResponse,
  GetPayslipEmployeeListParams,
  GetPayslipEmployeeListResponse,
  GetReplyMessageHistoryEmployeeListParams,
  GetReplyMessageHistoryEmployeeListResponse,
  GetTimesheetEmployeeListParams,
  GetTimesheetEmployeeListResponse,
  GetYTDHistoryEmployeeListParams,
  GetYTDHistoryEmployeeListResponse,
  GetRPNUpdateEmployeeListParams,
  GetRPNUpdateEmployeeListResponse,
} from '../types';

export const getEmployeeList = createAsyncThunk<
  GetEmployeeListResponse,
  GetEmployeeListParams
>(
  'employee/getEmployeeList',
  async (params: GetEmployeeListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.employee.getV2, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getEmployeeById = createAsyncThunk<GetEmployeeByIdResponse, string>(
  'employee/getEmployeeById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(`${endPoints.employee.get}/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPayrollSettingByEmployeeId = createAsyncThunk<
  GetPayrollSettingByEmployeeIdResponse,
  string
>(
  'employee/getPayrollSettingByEmployeeId',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.employee.payrollSetting}/${id}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getHolidayPayEmployeeList = createAsyncThunk<
  GetHolidayPayEmployeeListResponse,
  GetHolidayPayEmployeeListParams & { id: string }
>(
  'employee/getHolidayPayEmployeeList',
  async (
    params: GetHolidayPayEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(endPoints.employee.holidayPay(id), {
        params: rest,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getYtdHistoryEmployeeList = createAsyncThunk<
  GetYTDHistoryEmployeeListResponse,
  GetYTDHistoryEmployeeListParams & { id: string }
>(
  'employee/getYtdHistoryEmployeeList',
  async (
    params: GetYTDHistoryEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(
        `${endPoints.employee.ytdHistory}/${id}`,
        {
          params: rest,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getLeaverHistoryEmployeeList = createAsyncThunk<
  GetLeaverHistoryEmployeeListResponse,
  GetLeaverHistoryEmployeeListParams & { id: string }
>(
  'employee/getLeaverHistoryEmployeeList',
  async (
    params: GetLeaverHistoryEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(
        endPoints.employee.leaverHistory(id),
        {
          params: rest,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getDocumentEmployeeList = createAsyncThunk<
  GetDocumentEmployeeListResponse,
  GetDocumentEmployeeListParams & { id: string }
>(
  'employee/getDocumentEmployeeList',
  async (
    params: GetDocumentEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(
        `${endPoints.employee.document}/${id}`,
        {
          params: rest,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getNoteEmployeeList = createAsyncThunk<
  GetNoteEmployeeListResponse,
  GetNoteEmployeeListParams & { id: string }
>(
  'employee/getNoteEmployeeList',
  async (
    params: GetNoteEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(`${endPoints.employee.note}/${id}`, {
        params: rest,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPaymentEmployeeList = createAsyncThunk<
  GetPaymentEmployeeListResponse,
  GetPaymentEmployeeListParams & { id: string }
>(
  'employee/getPaymentEmployeeList',
  async (
    params: GetPaymentEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(
        `${endPoints.employee.paymentsSetting}/${id}`,
        {
          params: rest,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getDeductionEmployeeList = createAsyncThunk<
  GetDeductionEmployeeListResponse,
  GetDeductionEmployeeListParams & { id: string }
>(
  'employee/getDeductionEmployeeList',
  async (
    params: GetDeductionEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(
        `${endPoints.employee.deductionSetting}/${id}`,
        {
          params: rest,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAssignmentEmployeeList = createAsyncThunk<
  GetAssignmentEmployeeListResponse,
  GetAssignmentEmployeeListParams & { id: string }
>(
  'employee/getAssignmentEmployeeList',
  async (
    params: GetAssignmentEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(
        `${endPoints.employee.assignment}/${id}`,
        {
          params: rest,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getTimesheetEmployeeList = createAsyncThunk<
  GetTimesheetEmployeeListResponse,
  GetTimesheetEmployeeListParams & { id: string }
>(
  'employee/getTimesheetEmployeeList',
  async (
    params: GetTimesheetEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(
        `${endPoints.employee.timesheet}/${id}`,
        {
          params: rest,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPayslipEmployeeList = createAsyncThunk<
  GetPayslipEmployeeListResponse,
  GetPayslipEmployeeListParams & { id: string }
>(
  'employee/getPayslipEmployeeList',
  async (
    params: GetPayslipEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(
        `${endPoints.employee.selfBill}/${id}`,
        {
          params: rest,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAttachmentOrderEmployeeList = createAsyncThunk<
  GetAttachmentOrderEmployeeListResponse,
  GetAttachmentOrderEmployeeListParams
>(
  'employee/getAttachmentOrderEmployeeList',
  async (params: GetAttachmentOrderEmployeeListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.attachmentOrder.main, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getExpenseEmployeeList = createAsyncThunk<
  GetExpenseEmployeeListResponse,
  GetExpenseEmployeeListParams & { id: string }
>(
  'employee/getExpenseEmployeeList',
  async (
    params: GetExpenseEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(
        `${endPoints.employee.expense}/${id}`,
        {
          params: rest,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getEmailLogEmployeeList = createAsyncThunk<
  GetEmailLogEmployeeListResponse,
  GetEmailLogEmployeeListParams & { id: string }
>(
  'employee/getEmailLogEmployeeList',
  async (
    params: GetEmailLogEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(
        `${endPoints.emailLog.subcontractor}/${id}`,
        {
          params: rest,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getActivityLogEmployeeList = createAsyncThunk<
  GetActivityLogEmployeeListResponse,
  GetActivityLogEmployeeListParams & { id: string }
>(
  'employee/getActivityLogEmployeeList',
  async (
    params: GetActivityLogEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(
        `${endPoints.activityLog.subcontractor}/${id}`,
        {
          params: rest,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getMessageHistoryEmployeeList = createAsyncThunk<
  GetMessageHistoryEmployeeListResponse,
  GetMessageHistoryEmployeeListParams & { id: string }
>(
  'employee/getMessageHistoryEmployeeList',
  async (
    params: GetMessageHistoryEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(
        `${endPoints.employee.message}/${id}`,
        {
          params: rest,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getReplyMessageHistoryEmployeeList = createAsyncThunk<
  GetReplyMessageHistoryEmployeeListResponse,
  GetReplyMessageHistoryEmployeeListParams & { id: number }
>(
  'employee/getReplyMessageHistoryEmployeeList',
  async (
    params: GetReplyMessageHistoryEmployeeListParams & { id: number },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(
        `${endPoints.message.all}/${id}/reply-message`,
        {
          params: rest,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getBIKListingEmployeeList = createAsyncThunk<
  GetBIKListingEmployeeListResponse,
  GetBIKListingEmployeeListParams
>(
  'employee/getBIKListingEmployeeList',
  async (params: GetBIKListingEmployeeListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(`${endPoints.employee.bik}`, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getExpensePotEmployeeList = createAsyncThunk<
  GetExpensePotEmployeeListResponse,
  GetExpensePotEmployeeListParams & { id: string }
>(
  'employee/getExpensePotEmployeeList',
  async (
    params: GetExpensePotEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(
        `${endPoints.employee.expensePot(id)}`,
        {
          params: rest,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const getRPNUpdateEmployeeList = createAsyncThunk<
  GetRPNUpdateEmployeeListResponse,
  GetRPNUpdateEmployeeListParams & { id: string }
>(
  'employee/getRPNUpdateEmployeeList',
  async (
    params: GetRPNUpdateEmployeeListParams & { id: string },
    { rejectWithValue },
  ) => {
    try {
      const { id, ...rest } = params;
      const response = await jwtAxios.get(
        `${endPoints.employee.rpn}/${id}`,
        { params: rest },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      } as any;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const employeeActs = {
  getEmployeeList,
  getEmployeeById,
  getPayrollSettingByEmployeeId,
  getHolidayPayEmployeeList,
  getYtdHistoryEmployeeList,
  getLeaverHistoryEmployeeList,
  getDocumentEmployeeList,
  getNoteEmployeeList,
  getPaymentEmployeeList,
  getDeductionEmployeeList,
  getAssignmentEmployeeList,
  getTimesheetEmployeeList,
  getPayslipEmployeeList,
  getAttachmentOrderEmployeeList,
  getExpenseEmployeeList,
  getEmailLogEmployeeList,
  getActivityLogEmployeeList,
  getMessageHistoryEmployeeList,
  getReplyMessageHistoryEmployeeList,
  getBIKListingEmployeeList,
  getExpensePotEmployeeList,
  getRPNUpdateEmployeeList,
};
